import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import hu from './locales/hu.json';
import en from './locales/en.json';

const translations = { hu, en };

const LanguageContext = createContext();

/**
 * explainit.hu is a Hungarian site on a .hu domain: Hungarian is the default for
 * every page, every visit.
 */
export const DEFAULT_LANGUAGE = 'hu';
export const SUPPORTED_LANGUAGES = ['hu', 'en'];

/**
 * Where an explicit language choice lives — **sessionStorage, not localStorage**.
 *
 * The rule: HU by default; picking EN in the switcher holds for the rest of that
 * browsing session (across every subpage); closing the site and coming back
 * starts from Hungarian again. sessionStorage expresses exactly that lifetime —
 * it survives navigation and reloads within a tab and dies when the tab closes.
 *
 * Two deliberate consequences:
 *
 *  - SEO. Google's renderer clears localStorage AND sessionStorage on every page
 *    load, so a crawl is always a fresh session and ALWAYS renders Hungarian.
 *    That matters because Google indexes the RENDERED text and infers language
 *    from visible content: whatever the storage-empty path renders is the only
 *    version Google ever indexes. Under the old localStorage 'en' default, this
 *    Hungarian site was indexed as English.
 *    Refs: developers.google.com/search/docs/crawling-indexing/javascript/fix-search-javascript
 *          developers.google.com/search/docs/specialty/international/managing-multi-regional-sites
 *
 *  - No browser sniffing. We do NOT read navigator.language(s). Google recommends
 *    against adjusting content language from browser settings, and the browser
 *    locale reports the OS/UI locale rather than reading preference — a Hungarian
 *    visitor on an English-language OS would be served the wrong language.
 *
 * Language is therefore HU unless a human explicitly asked for EN, this session.
 */
export const SESSION_LANGUAGE_KEY = 'language';

/**
 * Read an explicit in-session language choice, falling back to Hungarian.
 *
 * Validates against SUPPORTED_LANGUAGES: a junk value (hand-edited storage, or a
 * stale key from an older build) must not reach `translations[language]`, where
 * it would resolve to undefined and make every t() lookup return its raw key path.
 */
function readSessionLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = window.sessionStorage.getItem(SESSION_LANGUAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    // Storage access can throw outright (Safari private mode, disabled storage).
    return DEFAULT_LANGUAGE;
  }
}

/**
 * Get nested value from object using dot notation
 * @param {object} obj - The object to search
 * @param {string} path - Dot-notated path like "hero.title"
 * @returns {string} The value or the path if not found
 */
function getNestedValue(obj, path) {
  const value = path.split('.').reduce((acc, part) => acc && acc[part], obj);
  return value !== undefined ? value : path;
}

/**
 * Replace template variables like {{year}} with actual values
 * @param {string} text - Text with {{variable}} placeholders
 * @param {object} variables - Object with variable values
 * @returns {string} Text with variables replaced
 */
function interpolate(text, variables = {}) {
  // Handle edge case where text is not a string (e.g., missing translation key)
  if (typeof text !== 'string') return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
}

export function LanguageProvider({ children }) {
  // Session-scoped, NOT persisted across visits — see SESSION_LANGUAGE_KEY.
  const [language, setLanguageState] = useState(readSessionLanguage);

  /**
   * Switch the UI language and remember it FOR THIS SESSION ONLY.
   *
   * Deliberately not a `useEffect` on `language`: an effect fires on mount too,
   * which would write the DEFAULT into storage on every first paint and promote
   * it into a stored "preference". That is exactly the bug the previous
   * implementation had — it wrote 'en' to localStorage for every visitor, so the
   * default became sticky and could never be changed for anyone who had already
   * visited. Persisting only here means storage records a genuine user choice and
   * nothing else.
   */
  const setLanguage = useCallback((next) => {
    setLanguageState(next);
    try {
      window.sessionStorage.setItem(SESSION_LANGUAGE_KEY, next);
    } catch {
      // Storage can throw (Safari private mode, disabled storage). The language
      // still switches for this render — only the session memory is lost.
    }
  }, []);

  // Keep the document's lang attribute in sync with the active UI language.
  // index.html ships lang="hu" and HU is now the default, so this is already
  // correct on first paint; this effect covers the case where the visitor
  // switches to EN. Required by WCAG 2.2 SC 3.1.1 (Language of Page): assistive
  // tech reads the live DOM, and a Hungarian TTS voice applying Hungarian
  // phonetics to English text is unintelligible.
  //
  // NOTE: this is an accessibility measure ONLY. It does nothing for SEO — Google
  // determines page language from the visible content and explicitly ignores
  // `lang` attributes and URLs. (An earlier comment here claimed an SEO benefit;
  // that was wrong.)
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  /**
   * Translation function - memoized for performance
   * @param {string} key - Dot-notated translation key like "hero.title"
   * @param {object} variables - Optional variables for interpolation
   * @returns {string} Translated text
   */
  const t = useCallback(
    (key, variables = {}) => {
      const translation = getNestedValue(translations[language], key);
      return interpolate(translation, variables);
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access translation functions and language state
 * @returns {{ language: string, setLanguage: function, t: function }}
 */
export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}
